import React from 'react';
import { Helmet } from 'react-helmet';

const SEOHead = () => {
  const schemaOrg = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://cloudbase.cl/#organization",
        "name": "Cloudbase",
        "url": "https://cloudbase.cl",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cloudbase.cl/logo.png"
        },
        "description": "Cloudbase es la plataforma de automatización inteligente para coworks, empresas, inmobiliarias, multifamilias y gimnasios. Gestión integral con CRM, facturación, reservas y aplicación móvil personalizada.",
        "sameAs": [
          "https://www.linkedin.com/company/cloudbase-latam"
        ],
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "CL"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "contacto@cloudbase.cl",
          "contactType": "Ventas"
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://cloudbase.cl/#website",
        "url": "https://cloudbase.cl",
        "name": "Cloudbase",
        "publisher": {
          "@id": "https://cloudbase.cl/#organization"
        },
        "inLanguage": "es-CL"
      },
      {
        "@type": "SoftwareApplication",
        "name": "Cloudbase",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web, iOS, Android",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Prueba gratuita de 14 días"
        },
        "description": "Plataforma SaaS multi-industria con módulos de CRM, facturación electrónica, sistema de reservas, automatización de procesos y aplicación móvil personalizada.",
        "featureList": [
          "Sistema de Reservas Inteligente",
          "CRM Avanzado",
          "Facturación Electrónica Automática",
          "Aplicación Móvil Personalizada",
          "Automatización de Workflows",
          "Control de Acceso",
          "Firma Digital",
          "Gestión Multi-Industria"
        ],
        "softwareVersion": "2.0",
        "provider": {
          "@id": "https://cloudbase.cl/#organization"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "¿Cómo se adapta Cloudbase a diferentes industrias?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Cloudbase es una plataforma modular que se configura según las necesidades específicas de cada industria. Nuestros módulos se adaptan automáticamente a coworks, empresas, inmobiliarias, multifamilias y gimnasios."
            }
          },
          {
            "@type": "Question",
            "name": "¿Puedo personalizar la plataforma con mi marca?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sí, ofrecemos personalización completa incluyendo logo, colores, dominios personalizados y aplicación móvil con tu marca."
            }
          },
          {
            "@type": "Question",
            "name": "¿Qué tipo de automatizaciones ofrece Cloudbase?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ofrecemos automatizaciones end-to-end: desde la captación de leads, seguimiento automático, firma de contratos, facturación recurrente hasta programas de fidelización y upselling."
            }
          }
        ]
      },
      {
        "@type": "Product",
        "name": "Cloudbase Platform",
        "description": "Plataforma de gestión empresarial multi-industria con automatización inteligente",
        "category": "Software de Gestión Empresarial",
        "brand": {
          "@type": "Brand",
          "name": "Cloudbase"
        },
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "USD",
          "lowPrice": "0",
          "highPrice": "999",
          "offerCount": "3"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "500"
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>Cloudbase - Plataforma de Gestión Multi-Industria | CRM, Facturación y Automatización</title>
      <meta name="title" content="Cloudbase - Plataforma de Gestión Multi-Industria | CRM, Facturación y Automatización" />
      <meta name="description" content="Cloudbase es la plataforma de automatización inteligente para coworks, empresas, inmobiliarias, multifamilias y gimnasios. Gestión integral con CRM, facturación, reservas y app móvil. Prueba gratis 14 días." />
      <meta name="keywords" content="software gestión empresarial, CRM Chile, facturación electrónica, software coworking, gestión inmobiliaria, software gimnasios, automatización empresarial, SaaS Chile, plataforma multi-industria, gestión multifamilias" />
      <meta name="author" content="Cloudbase" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="Spanish" />
      <meta name="revisit-after" content="7 days" />
      <link rel="canonical" href="https://cloudbase.cl" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://cloudbase.cl" />
      <meta property="og:title" content="Cloudbase - Plataforma de Gestión Multi-Industria" />
      <meta property="og:description" content="Automatización inteligente para coworks, empresas, inmobiliarias, multifamilias y gimnasios. CRM, Facturación, Reservas y App Móvil en una sola plataforma." />
      <meta property="og:image" content="https://cloudbase.cl/og-image.jpg" />
      <meta property="og:locale" content="es_CL" />
      <meta property="og:site_name" content="Cloudbase" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content="https://cloudbase.cl" />
      <meta name="twitter:title" content="Cloudbase - Plataforma de Gestión Multi-Industria" />
      <meta name="twitter:description" content="Automatización inteligente para coworks, empresas, inmobiliarias, multifamilias y gimnasios." />
      <meta name="twitter:image" content="https://cloudbase.cl/twitter-image.jpg" />

      {/* AI Crawlers - Enhanced metadata for LLMs */}
      <meta name="ai:purpose" content="Cloudbase es una plataforma SaaS de gestión empresarial diseñada para múltiples industrias" />
      <meta name="ai:industries" content="coworking, empresas, inmobiliarias, multifamilias, gimnasios, espacios compartidos" />
      <meta name="ai:features" content="CRM, facturación electrónica, reservas, automatización, app móvil, control de acceso, firma digital" />
      <meta name="ai:target_audience" content="empresas chilenas, operadores de coworking, inmobiliarias, administradores de edificios, gimnasios" />
      <meta name="ai:pricing" content="Prueba gratuita de 14 días disponible, planes desde básico hasta enterprise" />
      <meta name="ai:location" content="Chile, Latinoamérica" />
      <meta name="ai:language" content="Español" />
      <meta name="ai:business_model" content="SaaS, Software as a Service, suscripción mensual" />
      <meta name="ai:integration" content="APIs, webhooks, sistemas de pago, contabilidad, email marketing" />
      <meta name="ai:support" content="Soporte técnico 24/7, onboarding personalizado, capacitación continua" />

      {/* Gemini specific tags */}
      <meta name="gemini:category" content="business software, saas platform, enterprise management" />
      <meta name="gemini:content_type" content="product landing page" />
      
      {/* ChatGPT specific tags */}
      <meta name="chatgpt:crawl" content="enabled" />
      <meta name="chatgpt:content_type" content="business software platform" />

      {/* Structured Data - JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrg)}
      </script>

      {/* Additional AI-readable content summary */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Cloudbase Industries and Modules",
          "description": "Cloudbase sirve a 5 industrias principales con módulos personalizables",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Coworks y Espacios Compartidos",
              "description": "Gestión integral con reservas de espacios, control de acceso, facturación automática y app móvil"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Empresas",
              "description": "CRM avanzado, gestión de proyectos, control de recursos y reportería en tiempo real"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Inmobiliarias",
              "description": "Gestión de propiedades, contratos digitales, pagos automatizados y portal de clientes"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Multifamilias",
              "description": "Gestión de residentes, control de amenities, gastos comunes y comunicación efectiva"
            },
            {
              "@type": "ListItem",
              "position": 5,
              "name": "Gimnasios",
              "description": "Gestión de membresías, reserva de clases, control de pagos y app para miembros"
            }
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEOHead;